/**
 * Le filet de sécurité du carnet : rien ne part du navigateur sans avoir
 * d'abord été écrit sur l'appareil.
 *
 * Tom (2026-09-17) : « je ne dois JAMAIS perdre ça ». Un événement, c'est un
 * wifi saturé, une 4G qui tombe dans un sous-sol, un téléphone qu'on verrouille
 * au milieu d'une phrase. L'ordre est donc toujours le même :
 *
 *   1. écrire dans `localStorage`, tout de suite, à chaque frappe ;
 *   2. essayer d'envoyer à l'API ;
 *   3. n'effacer la copie locale QUE lorsque le serveur a confirmé.
 *
 * Une note créée hors ligne reçoit un identifiant local (`local-…`) et sera
 * créée sur le serveur à la première occasion. L'écran, lui, ne fait jamais
 * attendre : il montre ce qu'il a.
 */

export type ActionNote = { id?: string | null; texte: string; echeance?: string | null; faite?: boolean };
export type PhotoNote = { url: string; type?: 'carte' | 'flyer' | 'stand' | 'autre' | null; ajoute_le?: string | null };

export type NoteCarnet = {
  id: string;
  evenement_id: string | null;
  evenement_nom?: string | null;
  rencontre_personne_id: string | null;
  pseudo?: string | null;
  nom: string | null;
  entreprise: string | null;
  fonction: string | null;
  email: string | null;
  telephone: string | null;
  linkedin: string | null;
  site_web: string | null;
  notes: string | null;
  rencontre_le: string;
  actions: ActionNote[];
  photos?: PhotoNote[];
  carte_texte?: string | null;
  carte_lue_le?: string | null;
  manquants?: string[];
  rappel?: string | null;
};

const CLE = 'eventmatch.carnet.brouillons';

type Brouillon = { note: NoteCarnet; modifie_le: number };

function lire(): Record<string, Brouillon> {
  try {
    return JSON.parse(localStorage.getItem(CLE) ?? '{}') as Record<string, Brouillon>;
  } catch {
    return {};
  }
}

function ecrire(tout: Record<string, Brouillon>): void {
  try {
    localStorage.setItem(CLE, JSON.stringify(tout));
  } catch {
    // Stockage plein ou navigation privée : on continue, la note reste en
    // mémoire pour la session et part quand même au serveur.
  }
}

export const estLocale = (id: string) => id.startsWith('local-');

export function nouvelIdLocal(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Enregistre le brouillon sur l'appareil. Appelé à CHAQUE frappe, sans débat. */
export function poserBrouillon(note: NoteCarnet): void {
  const tout = lire();
  tout[note.id] = { note, modifie_le: Date.now() };
  ecrire(tout);
}

/** Le serveur a confirmé : la copie locale n'a plus de raison d'être. */
export function oublierBrouillon(id: string): void {
  const tout = lire();
  delete tout[id];
  ecrire(tout);
}

/** Une note locale vient d'être créée sur le serveur : elle change d'identité. */
export function renommerBrouillon(ancienId: string, nouvelId: string): void {
  const tout = lire();
  const b = tout[ancienId];
  if (!b) return;
  delete tout[ancienId];
  tout[nouvelId] = { note: { ...b.note, id: nouvelId }, modifie_le: b.modifie_le };
  ecrire(tout);
}

export function brouillons(): NoteCarnet[] {
  return Object.values(lire())
    .sort((a, b) => b.modifie_le - a.modifie_le)
    .map((b) => b.note);
}

export function brouillon(id: string): NoteCarnet | null {
  return lire()[id]?.note ?? null;
}

/**
 * La liste à afficher : ce que le serveur connaît, complété et corrigé par ce
 * que l'appareil a de plus récent. En cas de doute, le brouillon local gagne —
 * c'est lui qui contient la phrase qu'on vient de taper.
 */
export function fusionner(duServeur: NoteCarnet[]): NoteCarnet[] {
  const locaux = lire();
  const carte = new Map<string, NoteCarnet>();
  for (const n of duServeur) carte.set(n.id, n);
  for (const [id, b] of Object.entries(locaux)) carte.set(id, { ...carte.get(id), ...b.note, id });
  return [...carte.values()].sort(
    (a, b) => new Date(b.rencontre_le).getTime() - new Date(a.rencontre_le).getTime(),
  );
}

/** Le corps attendu par l'API, depuis une note d'écran. */
export function versApi(n: NoteCarnet) {
  return {
    evenementId: n.evenement_id,
    rencontrePersonneId: n.rencontre_personne_id,
    nom: n.nom,
    entreprise: n.entreprise,
    fonction: n.fonction,
    email: n.email,
    telephone: n.telephone,
    linkedin: n.linkedin,
    siteWeb: n.site_web,
    notes: n.notes,
    rencontreLe: n.rencontre_le,
    actions: (n.actions ?? []).filter((a) => a.texte?.trim()).map((a) => ({ texte: a.texte, echeance: a.echeance ?? null, faite: Boolean(a.faite) })),
    photos: n.photos ?? [],
  };
}

export function noteVide(evenementId: string | null): NoteCarnet {
  return {
    id: nouvelIdLocal(),
    evenement_id: evenementId,
    rencontre_personne_id: null,
    nom: null,
    entreprise: null,
    fonction: null,
    email: null,
    telephone: null,
    linkedin: null,
    site_web: null,
    notes: null,
    rencontre_le: new Date().toISOString(),
    actions: [],
    photos: [],
  };
}
